'use client'

import type { FormAction } from '@/core/action'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useActionState, useLayoutEffect, useMemo } from 'react'
import { LuSend } from 'react-icons/lu'
import { Button } from '../components/button'
import { SubmitButton } from '../components/submit-button'
import { useToast } from '../components/use-toast'
import { AutoForm, type AutoFormSchema, Form } from '../form'

type ResetPasswordFormProps = {
  action: FormAction<boolean>
}

export const ResetPasswordForm = ({ action }: ResetPasswordFormProps) => {
  const t = useTranslations('pages.resetPassword')
  const { toast } = useToast()

  const [state, formAction] = useActionState(action, null)

  useLayoutEffect(() => {
    if (state?.errors.length) {
      toast({
        variant: 'destructive',
        title: t('form.toasts.invalidLogin.title'),
        description: t('form.toasts.invalidLogin.description'),
      })
    }
  }, [state, toast, t])

  const schema: AutoFormSchema = useMemo(
    () => [
      {
        path: 'email',
        component: 'email',
        classNames: { root: 'mb-4' },
        options: {
          label: t('form.fields.email.label'),
          placeholder: t('form.fields.email.placeholder'),
        },
      },
    ],
    [t],
  )

  if (state?.success) {
    return (
      <div className="flex flex-col space-y-2 text-center">
        <LuSend className="w-16 h-16 text-pink-700 mx-auto" />
        <h1 className="text-2xl font-semibold tracking-tight">{t('success.title')}</h1>

        <p className="px-8 text-center text-normal text-muted-foreground pb-6">
          {t('success.description')}
        </p>

        <Button variant="ghost" asChild>
          <Link href="/auth/sign-in">{t('actions.init')}</Link>
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col space-y-2 text-center mb-4">
        <Image
          src="/logo.svg"
          priority
          alt="Noz Experience Hub"
          width={294}
          height={53}
          className="mx-auto mb-16"
        />
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="px-8 text-center text-sm text-muted-foreground">{t('description')}</p>
      </div>

      <Form state={state} action={formAction}>
        <AutoForm schema={schema} />
        <SubmitButton
          className="w-full mt-6"
          loadingText={t('form.actions.loading')}
          actionText={t('form.actions.send')}
        />
      </Form>
    </>
  )
}
